import * as turf from '@turf/turf';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {LibraryDataset, OverlayDataset} from 'app/components/Library/LibraryTypes';
import {
  EPA_SUPERFUND_SITES_FEATURE_COLLECTION_ID,
  NATIONAL_WETLANDS_INVENTORY_FEATURE_COLLECTION_ID,
  PLSS_FEATURE_COLLECTION_ID,
  US_PROTECTED_AREAS_FEATURE_COLLECTION_ID,
} from 'app/components/Library/overlaysConfig';
import {useOverlays} from 'app/components/Library/OverlaysProvider';
import api from 'app/modules/Remote/api';
import {HydratedFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser, ApiOverlay} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {REGRID_FEATURE_COLLECTION_ID} from 'app/utils/constants';

import {FeatureId, NumberedFeatureCollection, useManageFeatures} from './ManageFeaturesProvider';
import ManageFeaturesView from './ManageFeaturesView';
import {DrawPropertiesState, useApplyFeatureEdit} from './ManagePropertiesDraw';

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  loggedInUserActions: LoggedInUserActions;
  firebaseToken: string | null;
}

/**
 * Attempt to derive a human-readable name from a clicked overlay feature's
 * properties. Falls back to "New Property <id>" when no recognisable name
 * field is present.
 */
function getOverlayFeatureName(properties: geojson.GeoJsonProperties, fallbackId: number): string {
  if (!properties) return `New Property ${fallbackId}`;
  const nameKeys = ['name', 'label', 'title'];
  for (const key of nameKeys) {
    const val = properties[key.toLowerCase()];
    if (typeof val === 'string' && val.trim()) return val.trim();
  }
  return `New Property ${fallbackId}`;
}

// Module scope keeps the reference stable for useApplyFeatureEdit's dependency array.
const nameNewParcelProperty = (id: FeatureId, feature: geojson.Feature) =>
  getOverlayFeatureName(feature.properties, id);

const UPSTREAM_PARCEL_FC_IDS = new Set([
  REGRID_FEATURE_COLLECTION_ID,
  NATIONAL_WETLANDS_INVENTORY_FEATURE_COLLECTION_ID,
  PLSS_FEATURE_COLLECTION_ID,
  EPA_SUPERFUND_SITES_FEATURE_COLLECTION_ID,
  US_PROTECTED_AREAS_FEATURE_COLLECTION_ID,
]);

const ManagePropertiesSelectParcels: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  organization,
  profile,
  loggedInUserActions,
  firebaseToken,
}) => {
  const {manageFeaturesState, manageFeaturesActions, loading} = useManageFeatures();
  const {overlays, upstreamOverlays, meta: overlaysMeta} = useOverlays();

  const [featureToEdit, setFeatureToEdit] = React.useState<null | geojson.Feature>(null);
  const [editMode, setEditMode] = React.useState<boolean>(false);

  // Org-specific overlays (user-uploaded, not Upstream System datasets) that are in use
  const orgOverlays: ApiOverlay[] = React.useMemo(
    () => (overlays.toJS() as ApiOverlay[]).filter((o) => o.uploadedBy !== 'Upstream System'),
    [overlays]
  );

  // The curated Upstream datasets available for parcel selection.
  const upstreamParcelOverlays: LibraryDataset<OverlayDataset>[] = React.useMemo(
    () =>
      upstreamOverlays.filter(
        (o): o is LibraryDataset<OverlayDataset> =>
          o !== null && UPSTREAM_PARCEL_FC_IDS.has(o.featureCollectionId)
      ),
    [upstreamOverlays]
  );

  // No overlay selected by default — user must explicitly pick one
  const [selectedOverlayId, setSelectedOverlayId] = React.useState<number | null>(null);
  const [selectedOverlayFc, setSelectedOverlayFc] =
    React.useState<HydratedFeatureCollection | null>(null);

  // Fetch the selected overlay's feature collection whenever the selection changes
  React.useEffect(() => {
    if (selectedOverlayId === null) {
      setSelectedOverlayFc(null);
      return;
    }
    api.featureCollections.fetch(selectedOverlayId).then((res) => {
      setSelectedOverlayFc(res.get('data') as unknown as HydratedFeatureCollection);
    });
  }, [selectedOverlayId]);

  const {setUploadedFeatureCollection} = manageFeaturesActions;
  const {uploadedFeatureCollection} = manageFeaturesState;

  // Get whatever the last feature's id was and add 1 to it. Can't rely just on using length
  // since features can be deleted
  const getNextId = (fc: NumberedFeatureCollection | null) => {
    if (!fc) {
      return 1;
    }
    return fc.features.length ? fc.features[fc.features.length - 1].id + 1 : 1;
  };

  useApplyFeatureEdit(featureToEdit, setUploadedFeatureCollection, nameNewParcelProperty);

  const onParcelClick = (
    newFeature: geojson.Feature<geojson.Polygon | geojson.MultiPolygon>,
    ev: mapboxgl.MapLayerMouseEvent
  ) => {
    // Avoid accidentally creating new features while editing an existing shape
    if (editMode) {
      return;
    }

    const shiftKeyPressed = ev.originalEvent.shiftKey;

    if (shiftKeyPressed) {
      // Add selection to latest edited feature
      setFeatureToEdit((existingFeatureToEdit) => {
        if (existingFeatureToEdit) {
          const unionedGeometry: geojson.Polygon | geojson.MultiPolygon =
            turf.union(
              turf.featureCollection([
                existingFeatureToEdit as geojson.Feature<geojson.Polygon | geojson.MultiPolygon>,
                newFeature,
              ])
            )?.geometry ||
            (existingFeatureToEdit.geometry as geojson.Polygon | geojson.MultiPolygon);

          // After joining the existing shape with the new feature a user selected, it's
          // possible to have interior rings or other types of undesired geometry form.
          // There's a check that prevents invalid geometry from being uploaded in
          // validateDrawnProperty. But re moving those interior rings and cleaning
          // multi polygons helps make more of the shapes a user can create with the overlay
          // uploader tool be valid and pass that check.

          // Flatten the multipolygon into its own polygons
          const flattenedFc = turf.flatten(unionedGeometry);

          // For each of those polygons, only keep the first array in coordinates.
          // Any subsequent arrays are interior rings which we do not want. If there are
          // multiple polygons, join them back together as one multipolygon.
          const cleanedFeature =
            flattenedFc.features.length === 1
              ? turf.polygon([flattenedFc.features[0].geometry.coordinates[0]])
              : turf.multiPolygon(flattenedFc.features.map((f) => [f.geometry.coordinates[0]]));

          return {
            ...existingFeatureToEdit,
            geometry: cleanedFeature.geometry,
          };
        } else {
          return newFeature;
        }
      });
    } else {
      // Add a new feature. Important that we set the id here so that when we
      // add to this feature, we find the correct id in the feature collection to
      // modify.
      setFeatureToEdit({
        ...newFeature,
        id: getNextId(uploadedFeatureCollection),
      });
    }
  };

  const drawPropertiesState: DrawPropertiesState = {
    editMode: editMode,
    setEditMode: setEditMode,
    featureToEdit: featureToEdit,
    setFeatureToEdit: setFeatureToEdit,
    onMapFeatureClick: onParcelClick,
    orgOverlays: orgOverlays,
    upstreamParcelOverlays: upstreamParcelOverlays,
    selectedOverlayId: selectedOverlayId,
    setSelectedOverlayId: setSelectedOverlayId,
    selectedOverlayFc: selectedOverlayFc,
  };

  return (
    <ManageFeaturesView
      profile={profile}
      organization={organization}
      manageFeaturesState={manageFeaturesState}
      manageFeaturesActions={manageFeaturesActions}
      loggedInUserActions={loggedInUserActions}
      mode={'parcel'}
      drawPropertiesState={drawPropertiesState}
      loading={loading}
      loadingOverlays={overlaysMeta.loading}
      firebaseToken={firebaseToken}
    />
  );
};

export default ManagePropertiesSelectParcels;
